import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import type { Assignment, AssignmentWorkOrderGroup } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { ActionButton, StatusBadge } from '../../components/ui/index.js';
import { formatNumber } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { useSampleTypes } from '../samples/sample-type-fields.js';
import { toneForSampleStatus } from '../samples/status.js';
import { AssignmentDates } from './AssignmentDates.js';
import { testName, toneForAssignmentStatus } from './status.js';

/**
 * Lab work as Work Order -> Sample -> Tests, shared by the work list (D326) and
 * My Tasks (D327). Presentation only: the order is always the server's, and
 * these pieces only cut it where the work order and the sample change.
 */

const workOrderOf = (row: Assignment) => row.sample.workOrderId ?? null;

/**
 * Cuts the server's order where the work order changes. Never re-sorts: the
 * order is the server's, so a page and the next one always agree.
 */
export function groupByWorkOrder(rows: readonly Assignment[], groups: readonly AssignmentWorkOrderGroup[]) {
  const result: { group: AssignmentWorkOrderGroup; rows: Assignment[] }[] = [];
  for (const row of rows) {
    const last = result[result.length - 1];
    if (last && last.group.id === workOrderOf(row)) {
      last.rows.push(row);
      continue;
    }
    const group = groups.find((entry) => entry.id === workOrderOf(row)) ?? {
      id: workOrderOf(row),
      number: row.sample.jobNumber,
      clientName: null,
      projectName: null,
      createdAt: null,
      sampleCount: 0,
      itemCount: 0,
      statusCounts: {},
    };
    result.push({ group, rows: [row] });
  }
  return result;
}

/** Cuts one group's rows where the sample changes, in the server's order. */
export function groupBySample(rows: readonly Assignment[]) {
  const result: { sample: Assignment['sample']; rows: Assignment[] }[] = [];
  for (const row of rows) {
    const last = result[result.length - 1];
    if (last && last.sample.id === row.sample.id) last.rows.push(row);
    else result.push({ sample: row.sample, rows: [row] });
  }
  return result;
}

/**
 * One work order's lab work: one table, one header row, and each sample a row
 * group headed by the sample once, its tests beneath it.
 *
 * On a phone the same rows stack: the test and its Open action, then the
 * assignee, priority, dates and status, each with its column's name, so
 * nothing needs a sideways scroll. The page itself never scrolls sideways.
 */
export function WorkTable({
  label,
  rows,
  showAssignee = true,
  action,
}: {
  readonly label: string;
  readonly rows: readonly Assignment[];
  /** False on My Tasks, where every row is the reader's own. */
  readonly showAssignee?: boolean;
  /** The row's action, given the row's accessible name; Open by default. */
  readonly action?: (row: Assignment, name: string) => ReactNode;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();
  const sampleTypes = useSampleTypes();
  const maySeeSamples = can(PERMISSIONS.SAMPLES_VIEW);
  const th = 'h-9 border-b border-default-200 bg-default-50 px-3 text-start text-caption font-semibold text-default-700';
  const td =
    'border-b border-default-100 px-3 py-2.5 align-top text-body text-default-900 max-sm:block max-sm:min-w-0 max-sm:border-0 max-sm:p-0';
  const assigneeHeader = t('work.assigneeColumn');
  const priorityHeader = t('work.priorityColumn');
  const datesHeader = t('work.datesColumn');
  const statusHeader = t('common.status');
  const headers = [
    t('work.testColumn'),
    ...(showAssignee ? [assigneeHeader] : []),
    priorityHeader,
    datesHeader,
    statusHeader,
  ];
  // The column's name above a value, on a phone only, where the header row
  // is not drawn.
  const cellLabel = (text: string) => (
    <span aria-hidden="true" className="block text-micro font-medium text-default-600 sm:hidden">
      {text}
    </span>
  );

  return (
    <div className="relative overflow-x-auto">
      <table aria-label={label} className="w-full border-collapse max-sm:block sm:min-w-[44rem]">
        <thead className="max-sm:sr-only">
          <tr>
            {headers.map((header) => (
              <th key={header} scope="col" className={th}>
                {header}
              </th>
            ))}
            <th scope="col" className={th}>
              <span className="sr-only">{t('common.actions')}</span>
            </th>
          </tr>
        </thead>
        {groupBySample(rows).map(({ sample, rows: items }) => (
          <tbody
            key={sample.id}
            data-testid="work-sample"
            data-sample={sample.sampleNumber}
            className="max-sm:block"
          >
            <tr className="max-sm:block">
              <th
                scope="rowgroup"
                colSpan={headers.length + 1}
                className="border-b border-default-100 px-3 pb-1.5 pt-3 text-start font-normal max-sm:block"
              >
                <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  {maySeeSamples ? (
                    <Link
                      to={`/samples/${sample.id}`}
                      dir="ltr"
                      className="measure font-semibold text-default-900 underline-offset-2 hover:text-primary-700 hover:underline"
                      data-testid="work-sample-link"
                    >
                      {sample.sampleNumber}
                    </Link>
                  ) : (
                    <span dir="ltr" className="measure font-semibold text-default-900">
                      {sample.sampleNumber}
                    </span>
                  )}
                  <span className="text-caption text-default-700">{sampleTypes.nameOf(sample.sampleType)}</span>
                  <StatusBadge
                    label={t(`status.sample.${sample.status}`)}
                    tone={toneForSampleStatus(sample.status)}
                    size="sm"
                  />
                  <span className="text-caption text-default-600">
                    {t('work.sampleTests', { total: formatNumber(items.length, { locale }) })}
                  </span>
                </span>
              </th>
            </tr>
            {items.map((row) => {
              const standard = row.test.referenceStandard ?? null;
              const primary = standard ?? row.test.code ?? '';
              const name = testName(row.test, locale);
              return (
                <tr
                  key={row.id}
                  className="transition-colors hover:bg-default-50 max-sm:grid max-sm:grid-cols-2 max-sm:gap-x-3 max-sm:gap-y-2 max-sm:border-b max-sm:border-default-100 max-sm:py-2.5 max-sm:pe-3 max-sm:ps-6"
                  data-testid="work-item"
                >
                  <td className={`${td} ps-6 max-sm:ps-0`}>
                    <div className="min-w-0">
                      <Link
                        to={`/work/${row.id}`}
                        dir="ltr"
                        className="measure block font-medium text-default-900 no-underline hover:text-primary-700 hover:underline"
                      >
                        {primary}
                        {row.pieceCode ? ` / ${row.pieceCode}` : ''}
                      </Link>
                      <span className="block truncate text-caption text-default-600">
                        {name}
                        {standard && row.test.code ? (
                          <>
                            {' · '}
                            <span dir="ltr" className="measure">
                              {row.test.code}
                            </span>
                          </>
                        ) : null}
                      </span>
                    </div>
                  </td>
                  {showAssignee ? (
                    <td className={`${td} max-sm:row-start-2`}>
                      {cellLabel(assigneeHeader)}
                      {row.assignedTo ? (
                        <span className="text-default-700">{row.assignedTo.displayName}</span>
                      ) : (
                        <span className="text-caption text-default-600">{t('work.unassigned')}</span>
                      )}
                    </td>
                  ) : null}
                  <td className={`${td} max-sm:row-start-2`}>
                    {cellLabel(priorityHeader)}
                    <span className="text-default-700">{t(`workOrderPriority.${row.priority}`)}</span>
                  </td>
                  <td className={`${td} max-sm:row-start-3`}>
                    {cellLabel(datesHeader)}
                    <AssignmentDates assignment={row} />
                  </td>
                  <td className={`${td} max-sm:row-start-3`}>
                    {cellLabel(statusHeader)}
                    <div className="min-w-0">
                      <StatusBadge
                        label={t(`status.test.${row.status}`)}
                        tone={toneForAssignmentStatus(row.status)}
                      />
                      {row.latestStop?.reason ? (
                        <span className="mt-1 block truncate text-caption text-default-600">
                          {row.latestStop.reason}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className={`${td} text-end max-sm:col-start-2 max-sm:row-start-1`}>
                    {action ? (
                      action(row, t('work.openNamed', { test: primary, sample: row.sample.sampleNumber }))
                    ) : (
                      <ActionButton
                        priority="tertiary"
                        size="sm"
                        onPress={() => navigate(`/work/${row.id}`)}
                        aria-label={t('work.openNamed', { test: primary, sample: row.sample.sampleNumber })}
                      >
                        {t('common.open')}
                      </ActionButton>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        ))}
      </table>
    </div>
  );
}
